import { useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';
import { DateTime } from 'luxon';

export interface ICertificationCounts {
  expired: number;
  expiring: number;
}

const CERTIFICATION_COUNTS_QUERY = gql`
  query CertificationCounts($expiredBefore: Date!, $expiringStart: Date!, $expiringEnd: Date!) {
    dashboard {
      certificationCounts(expiredBefore: $expiredBefore, expiringStart: $expiringStart, expiringEnd: $expiringEnd) {
        expired
        expiring
      }
    }
  }
`;

const EXPIRING_WINDOW_DAYS = 30;

export default function useCertificationCounts(enabled: boolean) {
  const graphQLClient = useGraphQLClient();
  const baseKey = useReactQueryBaseKey();

  const expiredBefore = DateTime.now().startOf('day').toISODate();
  const expiringStart = DateTime.now().toISODate();
  const expiringEnd = DateTime.now().plus({ days: EXPIRING_WINDOW_DAYS }).toISODate();

  const { data, isLoading } = useQuery({
    queryKey: [...baseKey, 'certifications-dashboard', { expiredBefore, expiringStart, expiringEnd }],
    queryFn: async () => {
      const result = await graphQLClient.request<{ dashboard: { certificationCounts: ICertificationCounts } }>({
        document: CERTIFICATION_COUNTS_QUERY,
        variables: { expiredBefore, expiringStart, expiringEnd },
      });
      return result.dashboard.certificationCounts;
    },
    enabled,
  });

  return {
    expired: data?.expired ?? 0,
    expiring: data?.expiring ?? 0,
    loading: enabled && isLoading,
  };
}
